<section class="mod-page" data-module="reports">
  <header class="page-header">
    <div>
      <p class="eyebrow">Analytics & reports</p>
      <h1>Performance monitoring</h1>
    </div>
    <div class="btn-row">
      <button type="button" class="btn primary" (click)="exportPdf()">Export PDF</button>
      <button type="button" class="btn" (click)="exportExcel()">Export Excel</button>
    </div>
  </header>

  @if (message()) {
    <p class="toast">{{ message() }}</p>
  }

  <div class="toolbar">
    <label>
      Department
      <select [(ngModel)]="department" (change)="officer = ''; expandedCase.set(null)">
        @for (d of departmentOptions(); track d) {
          <option [value]="d">{{ d }}</option>
        }
      </select>
    </label>
    <label>
      Officer
      <select [(ngModel)]="officer" (change)="expandedCase.set(null)">
        <option value="" disabled>Select an officer&hellip;</option>
        @for (opt of officerOptions(); track opt.officer) {
          <option [value]="opt.officer">{{ opt.label }}</option>
        }
      </select>
    </label>
  </div>

  @if (!selectedOfficer()) {
    <div class="stat-strip">
      <div class="stat-strip-item tone-blue">
        <span class="stat-strip-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z"/></svg>
        </span>
        <div>
          <span class="stat-strip-label">Open cases</span>
          <strong class="stat-strip-value">{{ analytics().openCases }}</strong>
        </div>
      </div>
      <div class="stat-strip-item tone-green">
        <span class="stat-strip-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/></svg>
        </span>
        <div>
          <span class="stat-strip-label">Closed cases</span>
          <strong class="stat-strip-value">{{ analytics().closedCases }}</strong>
        </div>
      </div>
      <div class="stat-strip-item tone-amber">
        <span class="stat-strip-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M16 3v4M8 3v4M3 11h18"/></svg>
        </span>
        <div>
          <span class="stat-strip-label">Upcoming hearings</span>
          <strong class="stat-strip-value">{{ analytics().upcomingHearings }}</strong>
        </div>
      </div>
      <div class="stat-strip-item tone-purple">
        <span class="stat-strip-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 5 5 19"/><circle cx="7" cy="7" r="2"/><circle cx="17" cy="17" r="2"/></svg>
        </span>
        <div>
          <span class="stat-strip-label">Grant rate</span>
          <strong class="stat-strip-value">{{ analytics().grantedRate }}%</strong>
        </div>
      </div>
    </div>

    <section class="panel officer-overview-panel">
      <h2 class="case-detail-heading">Officers &amp; caseload</h2>
      @if (filteredPerformance().length) {
        <div class="officer-ov-grid">
          @for (row of filteredPerformance(); track row.officer; let i = $index) {
            <article
              class="officer-ov-card"
              [style.--accent]="accentColor(i)"
              (click)="selectOfficerCard(row.officer)"
            >
              <div class="officer-ov-head">
                <span class="officer-avatar">{{ initials(row.officer) }}</span>
                <div>
                  <h3>{{ row.officer }}</h3>
                  <p class="muted">{{ roleLabel(row.role) }} &middot; {{ row.department }}</p>
                </div>
              </div>
              <div class="officer-ov-stats">
                <div class="officer-ov-stat">
                  <span class="officer-ov-stat-label">
                    <svg class="stat-icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z"/></svg>
                    Total cases
                  </span>
                  <strong>{{ row.load }}</strong>
                </div>
                <div class="officer-ov-stat">
                  <span class="officer-ov-stat-label">
                    <svg class="stat-icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/></svg>
                    Closed
                  </span>
                  <strong>{{ row.resolved }}</strong>
                </div>
              </div>
            </article>
          }
        </div>
      } @else {
        <p class="muted">No officers in this department.</p>
      }
    </section>
  }

  @if (selectedOfficer(); as row) {
    <section class="panel officer-spotlight">
      <header class="spotlight-head">
        <span class="officer-avatar lg">{{ initials(row.officer) }}</span>
        <div>
          <h2>{{ row.officer }}</h2>
          <p class="muted">{{ row.role }} &middot; {{ row.department }}</p>
        </div>
        <span class="live-tag">Live</span>
      </header>
      <div class="spotlight-stats">
        <div class="spotlight-stat">
          <span class="stat-with-icon">
            <svg class="stat-icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z"/></svg>
            Performance load
          </span>
          <strong>{{ row.load }}</strong>
        </div>
        <div class="spotlight-stat">
          <span class="stat-with-icon">
            <svg class="stat-icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>
            Pending
          </span>
          <strong>{{ row.pending }}</strong>
        </div>
        <div class="spotlight-stat">
          <span class="stat-with-icon">
            <svg class="stat-icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/></svg>
            Resolved
          </span>
          <strong>{{ row.resolved }}</strong>
        </div>
        <div class="spotlight-stat">
          <span class="stat-with-icon">
            <svg class="stat-icon-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 5 5 19"/><circle cx="7" cy="7" r="2"/><circle cx="17" cy="17" r="2"/></svg>
            Resolution rate
          </span>
          <strong>{{ resolutionRate(row) }}%</strong>
        </div>
      </div>
      <div class="load-track lg"><div class="load-fill" [style.width.%]="resolutionRate(row)"></div></div>
      <p class="muted spotlight-note">Recalculated live from this officer's current grievance assignments.</p>
    </section>

    <section class="panel case-detail-panel">
      <h2 class="case-detail-heading">Cases, deadlines &amp; filed reports</h2>
      @if (officerCases().length) {
        <div class="case-mini-grid">
          @for (c of officerCases(); track c.id) {
            <article
              [class]="'case-mini-card tone-' + deadlineTone(c)"
              [class.expanded]="expandedCase() === c.id"
              (click)="toggleCase(c.id)"
            >
              <div class="case-mini-head">
                <span class="case-mini-number">#{{ c.trackingId }}</span>
                @switch (deadlineTone(c)) {
                  @case ('overdue') {
                    <span class="deadline-pill overdue">Overdue &middot; {{ c.investigationDueAt | date: 'd MMM y' }}</span>
                  }
                  @case ('soon') {
                    <span class="deadline-pill soon">Due soon &middot; {{ c.investigationDueAt | date: 'd MMM y' }}</span>
                  }
                  @case ('set') {
                    <span class="deadline-pill set">{{ c.investigationDueAt | date: 'd MMM y' }}</span>
                  }
                  @default {
                    <span class="deadline-pill none">No deadline</span>
                  }
                }
              </div>

              @if (expandedCase() === c.id) {
                <div class="case-mini-detail">
                  <h3>{{ c.subject }}</h3>
                  <div class="case-mini-meta">
                    <span class="pill" [class.mute]="c.status === 'Closed'" [class.warn]="c.status !== 'Closed'">{{ c.status }}</span>
                    <span class="dept-pill">{{ c.department }}</span>
                  </div>
                  <dl class="case-mini-fields">
                    <div>
                      <dt>Submitted by</dt>
                      <dd>{{ citizenFor(c) }}</dd>
                    </div>
                  </dl>
                  <div class="case-detail-report">
                    <span class="case-detail-report-label">Filed report</span>
                    @if (c.notes) {
                      <p>{{ c.notes }}</p>
                    } @else {
                      <p class="muted">No investigation report filed yet.</p>
                    }
                  </div>
                </div>
              } @else {
                <p class="case-mini-hint">Tap for details</p>
              }
            </article>
          }
        </div>
      } @else {
        <p class="muted">No cases are currently assigned to this officer.</p>
      }
    </section>
  } @else {
    <section class="panel officer-placeholder">
      <p class="muted">Pick an officer above to view their live performance monitoring.</p>
    </section>
  }
</section>
